import { styled, Theme } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import { Card, Box, Typography, CSSObject, Tabs, Tab } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { DataGrid } from '@mui/x-data-grid';

interface AppBarProps extends MuiAppBarProps {
	open?: boolean;
}

export const StyledIconButton = styled(IconButton)({
	borderRadius: '50%',
	transition: 'background-color 0.5s ease-in-out, color 0.3s ease-in-out',
	'&:hover': {
		color: '#fff',
		backgroundColor: '#EC008B', // Set your desired hover background color here
	},
});

export const StyledIconButtonWithBackground = styled(IconButton)({
	borderRadius: '50%',
	backgroundColor: '#20B5261A',
	transition: 'background-color 0.5s ease-in-out, color 0.3s ease-in-out',
	'&:hover': {
		color: '#fff',
		backgroundColor: '#EC008B', // Set your desired hover background color here
	},
});

export const StyledButton = styled(Button)({
	transition: 'background-color 0.5s ease-in-out, color 0.3s ease-in-out',
	color: '#fff',
	backgroundColor: '#EC008B',
	'&:hover': {
		backgroundColor: '#EC008B',
		color: '#000', // Set your desired hover color here
	},
});

export const StyledButtonInverse = styled(Button)({
	transition: 'color 0.5s ease-in-out background-color 0.5s ease-in-out',
	color: '#000',
	backgroundColor: '#fff',
	margin: '0 10px',
	'&:hover': {
		backgroundColor: '#EC008B',
		color: '#fff', // Set your desired hover color here
	},
});

export const StyledLink = styled(Link)({
	transition: 'color 0.5s ease-in-out background-color 0.5s ease-in-out',
	color: '#fff',
	backgroundColor: '#EC008B',
	padding: '10px 20px',
	textDecoration: 'none',
	borderRadius: '5px',

	'&:hover': {
		backgroundColor: '#F1F0E9',
		color: '#000', // Set your desired hover color here
	},
});

export const StyledLinkInverse = styled(Link)({
	transition: 'background-color 0.5s ease-in-out, color 0.3s ease-in-out',
	color: '#000',
	backgroundColor: '#fff',
	padding: '10px 10px',
	textDecoration: 'none',
	borderRadius: '5px',
	margin: '0 3px',

	'&:hover': {
		backgroundColor: '#EC008B',
		color: '#fff', // Set your desired hover color here
	},
});

export const StyledCheckbox = styled('input')({
	appearance: 'none',
	width: '16px',
	height: '16px',
	border: '1px solid #ccc',
	borderRadius: '4px',
	position: 'relative',
	cursor: 'pointer',
	'&:checked': {
		backgroundColor: '#EC008B',
		borderColor: '#EC008B',
	},
	'&:checked::after': {
		content: '""',
		position: 'absolute',
		top: '0px',
		left: '5px',
		width: '3px',
		height: '8px',
		border: 'solid white',
		borderWidth: '0 2px 2px 0',
		transform: 'rotate(45deg)',
	},
});
export const StyledCard = styled(Card)({
	maxWidth: 312,
	maxHeight: 407,
	transition: 'box-shadow 0.3s ease-in-out',
	'&:hover': { boxShadow: '0px 0px 10px 1px #20B52652', outline: '1.5px solid #EC008B' },
});

export const StyledinStock = styled('span')({
	display: 'inline-block',
	color: '#2C742F',
	borderRadius: '4px',
	width: '71px',
	height: '29px',
	padding: '4px 8px',
	fontWeight: '400',
	backgroundColor: '#20B52633',
});

//====================================================================
//				APP Bar
//====================================================================

export const AppBar = styled(MuiAppBar, {
	shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
	zIndex: theme.zIndex.drawer + 1,
	transition: theme.transitions.create(['width', 'margin'], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	...(open && {
		marginLeft: drawerWidth,
		// width: `calc(100% - ${drawerWidth}px)`,
		transition: theme.transitions.create(['width', 'margin'], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
	}),
}));

export const drawerWidth = 240;

export const openedMixin = (theme: Theme): CSSObject => ({
	width: drawerWidth,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: 'hidden',
});

export const closedMixin = (theme: Theme): CSSObject => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: 'hidden',
	width: `calc(${theme.spacing(7)} + 1px)`,
	[theme.breakpoints.up('sm')]: {
		width: `calc(${theme.spacing(8)} + 1px)`,
	},
});

export const DrawerHeader = styled('div')(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	padding: theme.spacing(0, 1),
	// necessary for content to be below app bar
	...theme.mixins.toolbar,
}));

export const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
	({ theme, open }) => ({
		width: drawerWidth,
		flexShrink: 0,
		whiteSpace: 'nowrap',
		boxSizing: 'border-box',
		...(open && {
			...openedMixin(theme),
			'& .MuiDrawer-paper': openedMixin(theme),
		}),
		...(!open && {
			...closedMixin(theme),
			'& .MuiDrawer-paper': closedMixin(theme),
		}),
	})
);

//====================================================================
//				APP Bar closed
//====================================================================

//====================================================================
//				Sales card start
//====================================================================

export const StyledSalseCard = styled(Card)({
	width: '100%',
	height: 150,
	border: '1px solid #39318A',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
	'&:hover': {
		transform: 'scale(1.05)',
		// boxShadow: '0 4px 8px rgba(0, 255, 0, 0.2)',
		boxShadow: '0 4px 8px rgb(0 0 0 / 20%)',
	},
});

export const StypedIconBox = styled(Box)({
	backgroundColor: 'white', // Use a default color or remove if not needed
	borderRadius: '50%',
	padding: '8px', // Use a default padding or remove if not needed
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});
//====================================================================
//				Sales card end
//====================================================================
//====================================================================
//				Best selling Product start
//====================================================================

export const StyledProductArticle = styled(Card)(({ theme }) => ({
	width: '100%',
	// maxWidth: 400,
	height: '100%',
	margin: theme.spacing(0),
	padding: theme.spacing(2),
	border: '1px solid #39318A',
	borderRadius: theme.shape.borderRadius,
	// boxShadow: theme.shadows[5],
	[theme.breakpoints.down('sm')]: {
		maxWidth: 300,
	},
	[theme.breakpoints.down('md')]: {
		width: '100%',
		maxWidth: 'none',
	},
}));

export const ProductCategory = styled(Typography)(({ theme }) => ({
	backgroundColor: theme.palette.background.paper,
	color: theme.palette.text.secondary,
	padding: theme.spacing(0.5),
	borderRadius: theme.shape.borderRadius,
	display: 'inline-block',
	marginBottom: theme.spacing(1),
}));

export const ProductDetails = styled(Box)(({ theme }) => ({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	marginBottom: theme.spacing(1),
	[theme.breakpoints.down('sm')]: {
		flexDirection: 'column',
		alignItems: 'flex-start',
	},
}));

export const ProductInfo = styled(Box)(({ theme }) => ({
	flex: 1,
	marginRight: theme.spacing(2),
	[theme.breakpoints.down('sm')]: {
		marginRight: 0,
		marginBottom: theme.spacing(1),
	},
}));

export const ProductStats = styled(Box)(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-end',
	[theme.breakpoints.down('sm')]: {
		alignItems: 'flex-start',
	},
}));

//===========================================================================
//  				category Card start
//===========================================================================

export const StyledCategoryCard = styled(Card)({
	maxWidth: 345,
	// margin: '16px',
	cursor: 'pointer',
	height: '100%',
});

export const StyledInput = styled('input')({
	width: '100%',
	padding: '10px',
	border: '1px solid #ccc',
	borderRadius: '4px',
	marginTop: '10px',
	fontSize: '16px',
	'&::placeholder': {
		color: '#999',
		fontSize: '14px',
	},
});
//====================================================================
//				Best selling Product end
//====================================================================
//====================================================================
//				Table start
//====================================================================

export const StyledTableRow = styled(TableRow)(() => ({
	'&:hover': {
		backgroundColor: '#e0f7fa', // Highlight color on hover
	},
}));

export const StyledTableCell = styled(TableCell)(({ status }: { status: string }) => ({
	backgroundColor:
		status === 'Unpaid'
			? '#fff3cd'
			: status === 'Paid'
				? '#d4edda'
				: status === 'Pending'
					? '#ffeeba'
					: 'transparent',
	color:
		status === 'Unpaid'
			? '#856404'
			: status === 'Paid'
				? '#155724'
				: status === 'Pending'
					? '#856404'
					: 'inherit',
}));

//===========================================================================
// 		Order page
//===========================================================================
export const StyledTypography = styled(Typography)(
	() => ({
		// Add your custom styles here
		fontSize: '2rem', // Example font size
		fontWeight: 'bold', // Example font weight
		color: '#333', // Example text color
		margin: '16px 0', // Example margin
		textAlign: 'center', // Example text alignment
	}),
	{
		// Set the default variant to 'h3'
		defaultProps: {
			variant: 'h3',
		},
	}
);

export const StyledDataGrid = styled(DataGrid)({
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#e0f7fa',
		fontWeight: '600',
		fontFamily: 'Poppins',
	},
	'& .MuiDataGrid-row': {
		backgroundColor: 'transparent',
		'&:hover': {
			backgroundColor: '#e0f7fa',
		},
	},
	'& .MuiDataGrid-columnHeaderTitleContainer': {
		'&:hover': {
			color: '#000',
		},
	},
	'@media print': {
		'.MuiDataGrid-toolbarContainer, .MuiDataGrid-filterForm, .MuiDataGrid-densitySelector, .MuiDataGrid-exportButton, .MuiDataGrid-footerContainer':
			{
				display: 'none',
			},
		'.MuiDataGrid-viewport': {
			overflow: 'visible !important',
		},
		'.MuiDataGrid-row': {
			pageBreakInside: 'avoid',
		},
	},
});

export const StyledTabs = styled(Tabs)({
	'& .MuiTabs-indicator': {
		backgroundColor: '#EC008B',
	},
});

export const StyledTab = styled(Tab)({
	backgroundColor: '#f7f7f7',
	color: '#000',
	'&.Mui-selected': {
		backgroundColor: '#EC008B',
		color: '#fff',
	},
	'&:hover': {
		backgroundColor: '#e0f7fa',
	},
});

// export const StyledButton = styled(Button)({
// 	marginTop: '16px',
// 	marginRight: '8px',
// });

// =====================================================================
export const StyledTableCellColumn = styled(TableCell)({
	fontWeight: '600',
	fontFamily: 'Poppins',
});

export const StyledIconButtonNav = styled(IconButton)({
	padding: '0px 8px 5px;',
	color: '#000',
	transition: 'color 0.3s ease, background-color 0.3s ease',
	'&:hover': {
		color: '#fff',
		backgroundColor: '#EC008B',
	},
});
export const StyledCrossIconButton = styled(IconButton)({
	padding: '4px',
	color: '#000',
	transition: 'background-color 0.3s ease-in-out, color 0.3s ease-in-out',
	'&:hover': {
		backgroundColor: '#898989',
		color: '#fff',
	},
});
